// EventFieldMaker.js
import { useEffect, useMemo, useRef, useState } from "react";
import FieldPreview from "./EventFieldPreview";
import EventFieldAttributes from "./EventFieldAttriutes";
import registerStyle from "manager/cmath/register/register.module.css"


const FIELD_TYPES = [
	"short_text",
	// "long_text",
	// "number",
	"select",
	// "multi_select",
	// "checkbox",
	// "radio",
	// "date",
	// "file",
	// "custom",
];

// 간단 슬러그 함수: 라벨 -> name(머신키)
const slugify = (s) =>
	(s || "")
		.toLowerCase()
		.replace(/[^\p{L}\p{N}]+/gu, "_")
		.replace(/^_+|_+$/g, "")
		.slice(0, 64);

function EventFieldMaker({ field, onPatch, onRemove }) {
	const {
		code,
		label = "",
		name = "",
		type = "",
		required = false,
		visibleIf = false,
		options = [],
		attributes = {},
		order = 0,
		customType = "",
		customConfig = {},
		hasAnswer = 0,
	} = field;

	// name 자동 생성 로직: 사용자가 직접 name을 수정하면 더는 자동 갱신 안 함
	const userEditedNameRef = useRef(Boolean(name));
	const lastAutoNameRef = useRef(name);
	useEffect(() => {
		if (!userEditedNameRef.current) {
			const auto = slugify(label);
			lastAutoNameRef.current = auto;
			onPatch(code, { name: auto });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [label]);

	const onChange = (patch) => onPatch(code, patch);

	// options 편집기
	const addOption = () => onChange({ options: [...options, ""] });
	const updateOption = (idx, val) => onChange({ options: options.map((o, i) => (i === idx ? val : o)) });
	const removeOption = (idx) => onChange({ options: options.filter((_, i) => i !== idx) });

	// attributes 편집기
	const updateAttributes = (key, val) => {
		const newAttributes = {
			...attributes,
			[key]: val,
		};

		if (val === "") {
			delete newAttributes[key];
		}

		onChange({ attributes: newAttributes });
	};

	// type에 따른 옵션 필요 여부
	const needsOptions = type === "select" || type === "multi_select" || type === "checkbox" || type === "radio";

	// customConfig 편집(문자열 <-> JSON)
	const [customConfigStr, setCustomConfigStr] = useState(
		customConfig && Object.keys(customConfig).length ? JSON.stringify(customConfig, null, 2) : "{}",
	);
	const [configError, setConfigError] = useState("");
	useEffect(() => {
		// 외부에서 customConfig가 바뀌면 textarea도 동기화
		setCustomConfigStr(customConfig && Object.keys(customConfig).length ? JSON.stringify(customConfig, null, 2) : "{}");
	}, [customConfig]);

	const handleConfigBlur = () => {
		try {
			const parsed = JSON.parse(customConfigStr || "{}");
			setConfigError("");
			onChange({ customConfig: parsed });
		} catch (e) {
			setConfigError("유효한 JSON이 아닙니다.");
		}
	};

	// 라벨 변경 시 name 자동 생성 중인지 표시(참고용)
	const nameIsAuto = useMemo(() => !userEditedNameRef.current, []);

    return(
        <div
            style={{
                display: "flex",
                flexDirection: "row-reverse",
                alignItems: "center",
                position: "relative",
                border: "1px solid #ddd",
                padding: "15px",
                borderRadius: "10px",
                marginBottom: "20px",
            }}
        >
            {/* 4) 삭제 */}
            {!hasAnswer && (
                <div
                    style={{
                        position: "absolute",
                        right: "10px",
                        top: "10px"
                    }}
                >
                    <button
                        type="button"
                        onClick={() => onRemove(code)}
                    >삭제</button>
                </div>
            )}

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignSelf: "flex-start",
                    width: "50%",
                    height: "100%",
                    marginLeft: "20px",
                    paddingTop: "10px"
                }}
            >
                <h4
                    style={{
                        margin: 0
                    }}
                >미리보기</h4>
                <div
                    style={{
                        border: "1px solid #aaa",
                        padding: "20px",
                        borderRadius: "15px",
                        minHeight: "200px"
                    }}
                >
                    <FieldPreview
                        label={label}
                        name={name}
                        type={type}
                        attributes={attributes}
                        options={options}
                    ></FieldPreview>
                </div>
            </div>

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignSelf: "flex-start",
                    width: "50%",
                    height: "100%",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center"
                    }}
                >
                    <div className={registerStyle['propertyWrap']}>
                        <div className={registerStyle['label']}>종류</div>
                        <div className={registerStyle['select']}>
                            <select
                                value={type}
                                onChange={(e) => {
                                    const nextType = e.target.value;
                                    // 타입 바뀔 때 옵션 필요 없으면 options 비우는 선택(선호도에 따라 유지 가능)
                                    if (
                                        nextType === "select" ||
                                        nextType === "multi_select" ||
                                        nextType === "checkbox" ||
                                        nextType === "radio"
                                    ) {
                                        onChange({ type: nextType, options: options?.length ? options : [""] });
                                    } else {
                                        onChange({ type: nextType, options: [] });
                                    }
                                }}
                            >
                                <option value="">선택</option>
                                {FIELD_TYPES.map((t) => (
                                    <option key={t} value={t}>
                                        {t}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className={registerStyle['propertyWrap']}>
                        <div className={registerStyle['label']}>필수</div>
                        <div className={registerStyle['input_checkbox']}>
                            <input
                                type="checkbox"
                                checked={!!required}
                                onChange={(e) => onChange({ required: e.target.checked })}
                            />
                        </div>
                    </div>

                    {/* <div className={registerStyle['propertyWrap']}>
                        <div className={registerStyle['label']}>표시 조건</div>
                        <div className={registerStyle['input_checkbox']}>
                            <input
                                type="checkbox"
                                checked={!!visibleIf}
                                onChange={(e) => onChange({ visibleIf: e.target.checked })}
                            />
                        </div>
                    </div> */}

                    <div className={registerStyle['propertyWrap']}>
                        <div className={registerStyle['label']}>순서</div>
                        <div className={registerStyle['input_text']}>
                            <input
                                type="number"
                                value={order ?? 0}
                                onChange={(e) => onChange({ order: Number(e.target.value) })}
                                min={0}
                            />
                        </div>
                    </div>
                </div>

                {/* 1) 기본 메타 */}
                <div>
                    <div className={registerStyle['propertyWrap']}>
                        <div className={registerStyle['label']}>질문</div>
                        <div className={registerStyle['input_text']} style={{width: "100%"}}>
                            <input
                                type="text"
                                value={label}
                                onChange={(e) => onChange({ label: e.target.value })}
                                placeholder="예: 이름을 입력해 주세요"
                            />
                        </div>
                    </div>

                    {/* <label style={{ display: "flex", flexDirection: "column" }}>
                        <span>Name (머신 키){nameIsAuto ? " — 자동생성" : ""}</span>
                        <div style={{ display: "flex", gap: 8 }}>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => {
                                    userEditedNameRef.current = true;
                                    onChange({ name: e.target.value });
                                }}
                                placeholder="예: name"
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    const auto = slugify(label);
                                    userEditedNameRef.current = false; // 다시 자동 모드로
                                    lastAutoNameRef.current = auto;
                                    onChange({ name: auto });
                                }}
                            >
                                자동
                            </button>
                        </div>
                    </label> */}
                </div>

                {/* 2) 속성 편집기 */}
                <EventFieldAttributes type={type} attributes={attributes} updateAttributes={updateAttributes} />

                {/* 3) 옵션 편집기 */}
                {needsOptions && (
                    <div style={{ marginTop: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h4 style={{ margin: 0 }}>옵션</h4>
                            <button type="button" onClick={addOption}>
                                + 옵션 추가
                            </button>
                        </div>
                        <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
                            {options.map((opt, i) => (
                                <div key={i} style={{ display: "flex", gap: 8 }}>
                                    <input
                                        type="text"
                                        value={opt}
                                        onChange={(e) => updateOption(i, e.target.value)}
                                        placeholder={`옵션 ${i + 1}`}
                                        style={{ flex: 1 }}
                                    />
                                    <button type="button" onClick={() => removeOption(i)}>
                                        삭제
                                    </button>
                                </div>
                            ))}
                            {!options?.length && <div style={{ color: "#999" }}>옵션이 없습니다. “+ 옵션 추가”를 눌러주세요.</div>}
                        </div>
                    </div>
                )}

                {/* 3) 커스텀 필드 설정 */}
                {type === "custom" && (
                    <div style={{ marginTop: 12 }}>
                        <h4 style={{ margin: "8px 0" }}>커스텀 필드 설정</h4>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                            <label style={{ display: "flex", flexDirection: "column" }}>
                                <span>customType</span>
                                <input
                                    type="text"
                                    value={customType || ""}
                                    onChange={(e) => onChange({ customType: e.target.value })}
                                    placeholder="예: address, phone 등"
                                />
                            </label>
                            <div style={{ gridColumn: "1 / -1" }}>
                                <label style={{ display: "flex", flexDirection: "column" }}>
                                    <span>customConfig (JSON)</span>
                                    <textarea
                                        rows={6}
                                        value={customConfigStr}
                                        onChange={(e) => setCustomConfigStr(e.target.value)}
                                        onBlur={handleConfigBlur}
                                        style={{ width: "100%", fontFamily: "ui-monospace, Menlo, Consolas, monospace" }}
                                    />
                                </label>
                                {configError && <div style={{ color: "crimson", marginTop: 4 }}>{configError}</div>}
                            </div>
                        </div>
                    </div>
                )}
            </div>
		</div>
	);
}

export default EventFieldMaker;
